import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import CardActions from '@material-ui/core/CardActions'
import Grid from '@material-ui/core/Grid'
import FormControl from '@material-ui/core/FormControl'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import TextField from '@material-ui/core/TextField'
import IconButton from '@material-ui/core/IconButton'
import DeleteIcon from '@material-ui/icons/Delete'
import Autosuggest from 'react-autosuggest'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import Paper from '@material-ui/core/Paper'
import CustomButton, { CancelCustomButton, ExtraLongCustomButton } from '../custom-material/button/Button'
import CustomTypography from '../custom-material/typography/Typography'
import { deburr, isEmpty } from 'lodash'
import DeactivateUsersDialogComponent from './DeactivateUsersDialogComponent'
import EmptyStateComponent from '../common/empty-state/EmptyStateComponent'

const styles = theme => ({
  card: {
    margin: 16,
    overflow: 'visible'
  },
  cardHeader: {
    backgroundColor: '#e1ecf8'
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 22
  },
  inputLabel: {
    color: '#003368',
    fontWeight: 600
  },
  contactHeadingContainer: {
    backgroundColor: '#DEDCD9',
    border: `2px solid #DEDCD9`,
    padding: 8
  },
  contactHeadingText: {
    color: '#213D72',
    fontWeight: 600,
    fontSize: 15
  },
  contactListGrid: {
    border: `2px solid #DEDCD9`,
    marginBottom: 8
  },
  contactListContainer: {
    borderBottom: `1px solid #DEDCD9`,
    '&:nth-of-type(even)': {
      backgroundColor: '#EDF3F8'
    },
    '&:last-child': {
      borderBottom: '0 !important'
    },
    '&:hover': {
      '& .trash-button': {
        visibility: 'visible !important'
      }
    }
  },
  contactListItem: {
    display: 'flex',
    alignItems: 'center',
    padding: 8
  },
  contactCountBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid #213D73`,
    borderRadius: 50,
    width: 20,
    height: 18,
    marginRight: 8,
    fontSize: 14,
    backgroundColor: '#213D73',
    color: 'white'
  },
  input: {
    fontSize: 15,
    paddingLeft: 3,
    paddingRight: 3
  },
  removalInput: {
    fontSize: 15,
    paddingLeft: 3,
    paddingRight: 3,
    outline: '1px solid rgba(0, 0, 0, 0.42)'
  },
  inputWithLabel: {
    fontSize: 15,
    outline: '1px solid rgba(0, 0, 0, 0.42)',
    marginTop: '18px !important',
    padding: 3
  },
  inputText: {
    overflow: 'hidden',
    paddingLeft: 3,
    paddingRight: 3
  },
  root: {
    height: 250,
    flexGrow: 1
  },
  container: {
    position: 'relative'
  },
  suggestionsContainerOpen: {
    position: 'absolute',
    zIndex: 1,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0
  },
  suggestion: {
    display: 'block'
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none'
  },
  selectInputBase: {
    outline: '1px solid rgba(0, 0, 0, 0.42)'
  },
  selectInputRoot: {
    fontSize: 15,
    height: 30,
    outline: '1px solid rgba(0, 0, 0, 0.42)'
  },
  selectInput: {
    paddingLeft: 3
  },
  selectMenu: {
    paddingLeft: 3,
    height: 16,
    display: 'flex',
    alignItems: 'center'
  },
  selectMenuItem: {
    fontSize: 15
  },
  selectMenuItemEmpty: {
    color: 'rgba(0, 0, 0, 0.42)',
    fontSize: 14
  },
  trashButton: {
    visibility: 'hidden !important'
  },
  trashIcon: {
    color: '#e52b2b'
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '16px 24px',
    marginTop: 16
  }
})

const renderInputComponent = inputProps => {
  const { classes, inputRef = () => {}, ref, ...other } = inputProps

  return (
    <TextField
      fullWidth
      InputProps={{
        inputRef: node => {
          ref(node)
          inputRef(node)
        },
        classes: {
          root: classes.selectInputRoot,
          input: classes.input
        }
      }}
      {...other}
    />
  )
}

const renderSuggestion = (suggestion, { query, isHighlighted }) => {
  const name = `${suggestion.firstName}${suggestion.lastName ? ` ${suggestion.lastName}` : ''}`
  const matches = match(name, query)
  const parts = parse(name, matches)

  return (
    <MenuItem selected={isHighlighted} component='div'>
      <div>
        {parts.map((part, index) => {
          return part.highlight ? (
            <span key={String(index)} style={{ fontWeight: 600, fontSize: 14 }}>
              {part.text}
            </span>
          ) : (
            <strong key={String(index)} style={{ fontWeight: 300, fontSize: 14 }}>
              {part.text}
            </strong>
          )
        })}
      </div>
    </MenuItem>
  )
}

const getSuggestions = (value, contactsCanDeactivate) => {
  const inputValue = deburr(value.trim()).toLowerCase()
  const inputLength = inputValue.length
  let count = 0

  return inputLength === 0
    ? []
    : contactsCanDeactivate.filter(suggestion => {
      const name = `${suggestion.firstName}${suggestion.lastName ? ` ${suggestion.lastName}` : ''}`
      const keep =
        count < 5 && name.slice(0, inputLength).toLowerCase() === inputValue

      if (keep) {
        count += 1
      }

      return keep
    })
}

const getSuggestionValue = suggestion => {
  return `${suggestion.firstName}${suggestion.lastName ? ` ${suggestion.lastName}` : ''}`
}

class DeactivateUsersComponent extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      dialogOpen: false,
      suggestions: this.props.contacts
        .filter(x => !x.pending &&
          this.props.contacts.filter(x => x.pending && x.operationType === 'Remove').map(x => x.emailAddress).indexOf(x.emailAddress) === -1
        )
        .filter(x => !this.props.contactsToDeactivate.map(y => y.emailAddress).includes(x.emailAddress)) || []
    }

    this.handleOpenDialog = () => this.setState({dialogOpen: true})
    this.handleCloseDialog = () => this.setState({dialogOpen: false})
    this.handleConfirmDialog = () => {
      this.setState({dialogOpen: false})
      this.props.resetContactsToDeactivate()
    }
    this.isValid = () => {
      for (let i = 0; i < this.props.contactsToDeactivate.length; i++) {
        if (!this.props.contactsToDeactivate[i].emailAddress ||
          !this.props.contactsToDeactivate[i].removalReason ||
          (this.props.contactsToDeactivate[i].removalReason === 'Other' && !this.props.contactsToDeactivate[i].removalExplanation)
        ) {
          return false
        }
      }
      return true
    }
    this.handleSuggestionsFetchRequested = contactsCanDeactivate => ({ value }) => {
      this.setState({
        suggestions: getSuggestions(value, contactsCanDeactivate)
      })
    }

    this.handleSuggestionsClearRequested = () => {
      this.setState({
        suggestions: []
      })
    }

    this.handleSuggestionSelected = contactToDeactivateIndex => (event, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }) => {
      this.props.selectSuggestion(contactToDeactivateIndex, suggestion.emailAddress, suggestionValue)
    }
  }

  render () {
    const { dialogOpen, suggestions } = this.state
    const { classes, contacts, contactsToDeactivate, comments, addContactToDeactivate, removeContactToDeactivate, updateContact, updateGlobalComments, saveDeactivate } = this.props
    const emailsPendingDeactivations = contacts.filter(x => x.pending && x.operationType === 'Remove').map(x => x.emailAddress)
    const contactsCanDeactivate = contacts.filter(x => !x.pending && emailsPendingDeactivations.indexOf(x.emailAddress) === -1).filter(x => !contactsToDeactivate.map(y => y.emailAddress).includes(x.emailAddress))
    const autosuggestProps = {
      renderInputComponent,
      suggestions: !isEmpty(suggestions) ? suggestions : contactsCanDeactivate,
      onSuggestionsFetchRequested: this.handleSuggestionsFetchRequested(contactsCanDeactivate),
      onSuggestionsClearRequested: this.handleSuggestionsClearRequested,
      getSuggestionValue,
      renderSuggestion,
      shouldRenderSuggestions: () => true
    }
    return (
      <Card className={classes.card}>
        <CardHeader className={classes.cardHeader} title={<div className={classes.title}><span>Deactivate Users</span></div>} />
        <CardContent>
          {isEmpty(contacts)
            ? <EmptyStateComponent title='No contact data found' />
            : <React.Fragment>
              <Grid container spacing={0}>
                <Grid style={{paddingLeft: 0, paddingRight: 0}} item xs={12}>
                  <CustomTypography variant='body1' paragraph>
                    {`Use the form below to request deactivation(s).
                    Choose the person(s) you would like to deactivate and the corresponding reason(s),
                    then click submit below.`}
                  </CustomTypography>
                </Grid>
                <Grid className={classes.contactHeadingContainer} item xs={12}>
                  <Grid container spacing={0}>
                    <Grid className={classes.contactHeadingText} item xs={3}>
                      Choose Subscriber(s):
                    </Grid>
                    <Grid className={classes.contactHeadingText} item xs={4}>
                      Choose Reason(s) for Deactivation:
                    </Grid>
                    <Grid className={classes.contactHeadingText} item xs={5} />
                  </Grid>
                </Grid>
                <Grid className={classes.contactListGrid} item xs={12}>
                  {isEmpty(contactsToDeactivate)
                    ? null
                    : contactsToDeactivate.map((contactToDeactivate, i) => (
                      <Grid className={classes.contactListContainer} key={i} container spacing={0}>
                        <Grid className={classes.contactListItem} item xs={3}>
                          <div className={classes.contactCountBadge}>{i + 1}</div>
                          <Autosuggest
                            {...autosuggestProps}
                            onSuggestionSelected={this.handleSuggestionSelected(i)}
                            inputProps={{
                              classes,
                              placeholder: 'Subscriber Name',
                              value: contactToDeactivate.name || '',
                              onChange: updateContact(i, 'name'),
                              required: true,
                              error: !contactToDeactivate.emailAddress
                            }}
                            theme={{
                              container: classes.container,
                              suggestionsContainerOpen: classes.suggestionsContainerOpen,
                              suggestionsList: classes.suggestionsList,
                              suggestion: classes.suggestion
                            }}
                            renderSuggestionsContainer={options => (
                              <Paper elevation={2} {...options.containerProps} square>
                                {options.children}
                              </Paper>
                            )}
                          />
                        </Grid>
                        <Grid className={classes.contactListItem} item xs={4}>
                          <FormControl fullWidth>
                            <Select
                              className={classes.selectInputBase}
                              value={contactToDeactivate.removalReason || ''}
                              onChange={updateContact(i, 'removalReason')}
                              MenuProps={{classes: {paper: classes.selectMenuPaper}}}
                              classes={{selectMenu: classes.selectMenu}}
                              required
                              error={!contactToDeactivate.removalReason}
                              displayEmpty
                              inputProps={{
                                name: `removal-reason-${i}`,
                                id: `removal-reason-select-${i}`,
                                classes: {
                                  root: classes.selectInputRoot
                                }
                              }}
                            >
                              <MenuItem value=''>
                                <span className={classes.selectMenuItemEmpty}>Removal Reason</span>
                              </MenuItem>
                              <MenuItem className={classes.selectMenuItem} value='No longer with company'>No longer with company</MenuItem>
                              <MenuItem className={classes.selectMenuItem} value='No longer with this division'>No longer with this division</MenuItem>
                              <MenuItem className={classes.selectMenuItem} value='No longer in this role'>No longer in this role</MenuItem>
                              <MenuItem className={classes.selectMenuItem} value='Content not relevant'>Content not relevant</MenuItem>
                              <MenuItem className={classes.selectMenuItem} value='Other'>Other</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid className={classes.contactListItem} item xs={4}>
                          {contactToDeactivate.removalReason === 'Other'
                            ? <TextField
                              id={`removal-explanation-${i}`}
                              placeholder='Other, please explain:*'
                              required
                              error={!contactToDeactivate.removalExplanation}
                              value={contactToDeactivate.removalExplanation || ''}
                              InputProps={{classes: {root: classes.removalInput, input: classes.inputText}}}
                              fullWidth
                              onChange={updateContact(i, 'removalExplanation')}
                            />
                            : null
                          }
                        </Grid>
                        <Grid className={classes.contactListItem} style={{alignItems: 'center', justifyContent: 'center'}} item xs={1}>
                          {i === 0 ? <div style={{width: 48, height: 48}} /> : <IconButton onClick={removeContactToDeactivate(i)} className={`${classes.trashButton} trash-button`} disableRipple><DeleteIcon className={classes.trashIcon} /></IconButton>}
                        </Grid>
                      </Grid>
                    ))
                  }
                </Grid>
                <Grid style={{paddingLeft: 0, paddingRight: 0}} item xs={12}>
                  <ExtraLongCustomButton onClick={addContactToDeactivate} variant='contained' size='small' color='secondary'>Deactivate More Users</ExtraLongCustomButton>
                  <br /><br />
                </Grid>
                <Grid style={{paddingLeft: 0, paddingRight: 0}} item xs={10}>
                  <TextField
                    id='comments'
                    label='Comments:'
                    value={comments}
                    InputProps={{disableUnderline: true, classes: {root: classes.inputWithLabel, input: classes.inputText}}}
                    InputLabelProps={{shrink: true, classes: {shrink: classes.inputLabel}}}
                    fullWidth
                    multiline
                    rows={2}
                    rowsMax={2}
                    onChange={updateGlobalComments}
                  />
                </Grid><Grid item xs={2} />
              </Grid>
            </React.Fragment>
          }
        </CardContent>
        {isEmpty(contacts)
          ? null
          : <CardActions className={classes.cardFooter}>
            <CancelCustomButton onClick={() => { this.props.resetContactsToDeactivate(); this.props.resetGlobalComments() }} variant='contained' color='default' size='small' >Cancel</CancelCustomButton>
            <CustomButton disabled={!this.isValid()} variant='contained' color='secondary' size='small'  onClick={this.handleOpenDialog} >Submit</CustomButton>
          </CardActions>
        }
        {dialogOpen
          ? <DeactivateUsersDialogComponent
            dialogOpen={dialogOpen}
            contactsToDeactivate={contactsToDeactivate.map(x => contacts.find(c => c.emailAddress === x.emailAddress))}
            handleCloseDialog={this.handleCloseDialog}
            handleConfirmDialog={this.handleConfirmDialog}
            saveDeactivate={saveDeactivate}
          />
          : null
        }
      </Card>
    )
  }
}

DeactivateUsersComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  contacts: PropTypes.array,
  contactsToDeactivate: PropTypes.array,
  comments: PropTypes.string,
  addContactToDeactivate: PropTypes.func,
  removeContactToDeactivate: PropTypes.func,
  resetContactsToDeactivate: PropTypes.func,
  updateContact: PropTypes.func,
  selectSuggestion: PropTypes.func,
  updateGlobalComments: PropTypes.func,
  resetGlobalComments: PropTypes.func
}

export default withStyles(styles)(DeactivateUsersComponent)
